/**
 * Tiny hex-color helpers used by the Business brand color picker.
 * - `darkenHex` produces the "strong" variant (move toward black).
 * - `lightenHex` produces the "soft" variant (move toward white).
 * Both accept and return uppercase 7-char hex strings like "#7266E8".
 */

const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));

const hexToRgb = (hex: string): [number, number, number] => {
    const s = hex.startsWith('#') ? hex.slice(1) : hex;
    return [
        parseInt(s.slice(0, 2), 16),
        parseInt(s.slice(2, 4), 16),
        parseInt(s.slice(4, 6), 16),
    ];
};

const rgbToHex = (r: number, g: number, b: number): string =>
    `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g)
        .toString(16)
        .padStart(2, '0')}${clamp(b).toString(16).padStart(2, '0')}`.toUpperCase();

/** Move `hex` `amount` (0..1) of the way toward black. */
export const darkenHex = (hex: string, amount = 0.18): string => {
    if (!hex || hex.length !== 7) return hex;
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
};

/** Move `hex` `amount` (0..1) of the way toward white. */
export const lightenHex = (hex: string, amount = 0.85): string => {
    if (!hex || hex.length !== 7) return hex;
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex(
        r + (255 - r) * amount,
        g + (255 - g) * amount,
        b + (255 - b) * amount
    );
};
